/**
 * @format
 * @flow strict-local
 */
// DEPENDENCIES

import parsePhoneNumber, {
	parsePhoneNumberFromString
} from "libphonenumber-js";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import React, {
	useEffect,
	useState
} from "react";
import {
	useNavigate,
	useSearchParams
} from "react-router-dom";

import {
	ComponentWrapper
} from "./SignUp.styled";

import Button from "../../components/Button";
import Card, {
	CardBody,
	CardFooter,
	CardHeader
} from "../../components/Card";
import InputText from "../../components/InputText";
import InputWrapper from "../../components/InputWrapper";

import routerHelper from "../../helpers/router";
import storeHelper from "../../helpers/store";
import utilsHelper from "../../helpers/utils";

import useApi from "../../hooks/api";
import useForm from "../../hooks/form";

import i18n from "../../i18n";

// COMPONENT

const SignUp = (props) => {
	const [
		error, 
		setError
	] = useState(null);
	const [
		isPhoneNumberValid, 
		setIsPhoneNumberValid
	] = useState(false);
	const [
		isRenderReady, 
		setIsRenderReady
	] = useState(false);
	const [
		phoneCountryCode, 
		setPhoneCountryCode
	] = useState("us");
	const [
		phoneNumber, 
		setPhoneNumber
	] = useState("");
	const [
		qs, 
		setQs
	] = useState(null);

	const {
		fetchApi
	} = useApi();
	const {
		eventHandler,
		getFormValues,
		getInputErrorText,
		hasError,
		initForm,
		inputConfigs,
		inputs,
		inputStates,
		updateInputConfigs,
		updateInputErrors,
		updateInputs,
		updateInputStates,
		validateForm
	} = useForm();
	const navigate = useNavigate();
	const [
		searchParams
	] = useSearchParams();

	// Lifecycle

	// Note - Run on bootstrap
	useEffect(
		() => {
			setQs(Object.fromEntries([
				...searchParams
			]));
	    }, 
		[]
	);

	// Note - Run when qs change
	useEffect(
		() => {
			if(!utilsHelper.isObject(qs)) {
				return;
			}

			initInputs();
	    }, 
		[qs]
	);

	// Note - Run when inputs change
	useEffect(
		() => {
			if(utilsHelper.isObjectEmpty(inputs)) {
				return;
			}

			if(!isRenderReady) {
				setIsRenderReady(true);
			}
		}, 
		[inputs]
	);

	// Events

	/**
	 * goToSignIn - ... pass qs
	 * 
	 * @param {object} e ... 
	 */
	const goToSignIn = (e) => {
		navigate({
			pathname: routerHelper.getUrl("signIn")
		});
	};

	/**
	 * phoneNumberChangeHandler - ... 
	 * 
	 * @param {string} phone ... 
	 * @param {object} country ... 
	 */
	const phoneNumberChangeHandler = (phone, country) => {
		setPhoneCountryCode(country.countryCode);
		setPhoneNumber(phone);

		const parsedNumber = parsePhoneNumberFromString(
			phone, 
			country.countryCode.toUpperCase()
		);

		if(parsedNumber) {
			setIsPhoneNumberValid(parsedNumber.isValid());
		} else {
			setIsPhoneNumberValid(false);
		}
	};

	/**
	 * submitHandler - ...
	 * 
	 * @param {object} e ... 
	 */
	const submitHandler = async (e) => {
		if(
			!isPhoneNumberValid || 
			!inputStates.isValid ||
			inputStates.isSubmit
		) {
			validateForm(false);

			return;
		}

		updateInputStates({
			isSubmit: true
		});

		const parsedPhoneNumber = parsePhoneNumberFromString(
			phoneNumber,
			phoneCountryCode.toUpperCase()
		);

		const data = getFormValues();
		const response = await fetchApi({
			method: "get",
			params: {
				app_version: "4.0.0", // Note - Need to bypass native app checkup
				device_id: "1", // Note - Need to bypass native app checkup
				device_os: "browser", // Note - Need to bypass native app checkup
				email: data.email,
				first_name: data.firstName,
				last_name: data.lastName,
				one_signal_user_id: "null", // Note - Need to bypass native app checkup
				password: data.password,
				phone: parsedPhoneNumber.nationalNumber,
				phone_country_code: phoneCountryCode,
				service: "signup"
			}
		});

		if(response.error) {
			switch(response.error.code) {
				default:
					setError(response.error.message || response.error.code);
				break;
			}

			updateInputStates({
				isSubmit: false,
				isValid: false
			});

			return;
		}

		setError(null);
		updateInputStates({
			isSubmit: false,
			isSubmitted: true
		});
	};

	// Misc

	/**
	 * initInputs - ...
	 */
	const initInputs = () => {
		const newForm = {
			email: {
				formatter: "lowercase",
				initialValue: "",
				inputErrorType: "email",
				inputType: "text",
				touched: false,
				validators: {
					email: true,
					required: true
				}
			},
			firstName: {
				formatter: "",
				initialValue: "",
				inputErrorType: "string",
				inputType: "text",
				touched: false,
				validators: {
					required: true
				}
			},
			lastName: {
				formatter: "",
				initialValue: "",
				inputErrorType: "string",
				inputType: "text",
				touched: false,
				validators: {
					required: true
				}
			},
			password: {
				formatter: "",
				initialValue: "",
				inputErrorType: "password",
				inputType: "text",
				touched: false,
				validators: {
					required: true
				}
			}
		};

		initForm(newForm);
	};

	// Render

	/**
	 * render - ...
	 */
	const render = () => {
		if(!isRenderReady) {
			return null;
		}

		let errorContent;
		let successContent;

		if(error) {
			errorContent = (
				<div
					style={{
						borderRadius: "8px",
						backgroundColor: "#F04124",
						color: "#fff",
						padding: "16px"
					}}
					>
					{error}
				</div>
			);
		} else if(inputStates.isSubmitted) {
			successContent = (
				<div
					style={{
						borderRadius: "8px",
						backgroundColor: "#20B651",
						color: "#fff",
						padding: "16px"
					}}
					>
					An email will be sent shortly with instructions on completing your signup.
				</div>
			);

			return (
				<ComponentWrapper>
					{successContent}
					<span>
						<a
							onClick={goToSignIn}
							>
							Sign in
						</a>
					</span>
				</ComponentWrapper>
			);
		}

		return (
			<ComponentWrapper>
				{errorContent}
				<Card
					className="card"
					>
					<CardHeader>
						<strong>
							Welcome to Copdate
						</strong>
						<p>
							Please create an account to sign up.
						</p>
					</CardHeader>
					<CardBody
						className="cardBody"
						>
						<InputWrapper
							label="First name"
							>
							<InputText
								name="firstName"
								onBlur={eventHandler}
								onChange={eventHandler}
								{...inputs.firstName}
							/>
						</InputWrapper>
						<InputWrapper
							label="Last name"
							>
							<InputText
								name="lastName"
								onBlur={eventHandler}
								onChange={eventHandler}
								{...inputs.lastName}
							/>
						</InputWrapper>
						<InputWrapper
							label="Email"
							>
							<InputText
								name="email"
								onBlur={eventHandler}
								onChange={eventHandler}
								type="email"
								{...inputs.email}
							/>
						</InputWrapper>
						<InputWrapper
							label="Password"
							>
							<InputText
								name="password"
								onBlur={eventHandler}
								onChange={eventHandler}
								type="password"
								{...inputs.password}
							/>
						</InputWrapper>
						<InputWrapper
							label="Phone"
							style={{
								width: "100%"
							}}
							>
							<PhoneInput
								buttonStyle={{
									borderColor: "#757575",
									borderRadius: "8px 0 0 8px",
									height: "48px",
									padding: "12px 16px"
								}}
								country="us"
								inputStyle={{
									borderColor: "#757575",
									borderRadius: "8px",
									height: "48px",
									padding: "12px 12px 12px 84px",
									width: "100%"
								}}
								onChange={phoneNumberChangeHandler}
								value={phoneNumber}
							/>
						</InputWrapper>
						<span>
							By tapping <strong>Sign up</strong>, you agree to Copdate's
							&nbsp;
							<a
								href="https://www.copdate.com/terms-of-service"
								>
								Terms of Use
							</a>
							&nbsp;
							and
							&nbsp;
							<a
								href="https://www.copdate.com/privacy-policy"
								>
								Privacy Policy
							</a>.
						</span>
					</CardBody>
					<CardFooter>
						<Button
							className="footerButton"
							disabled={
								!isRenderReady ||
								!isPhoneNumberValid || 
								inputStates.isSubmit ||
								!inputStates.isValid
							}
							label="Sign up"
							loaderActive={inputStates.isSubmit}
							loaderEnable
							onClick={submitHandler}
							paletteColor={inputStates.isValid && isPhoneNumberValid ? "dark" : "disabled"}
							type="submit"
						/>
					</CardFooter>
				</Card>
				<span>
					Have an account?
					&nbsp;
					<a
						onClick={goToSignIn}
						>
						Sign in
					</a>
				</span>
			</ComponentWrapper>
		);
	};

	return render();
};

// EXPORT

export default SignUp;
